import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BannerCanvas } from "@/components/banner/BannerCanvas";
import { SlotPicker } from "@/components/banner/SlotPicker";
import { SlotList, Legend } from "@/components/banner/SlotList";
import { distribute, freeSpot, numbering, overlaps, ratioOf, respace, termStatus, uid } from "@/lib/bannerLayout";
import { Minus, Plus, WandSparkles, SquareDashed, Trash2, UserRound, Loader2, FileDown } from "lucide-react";
import { downloadBlob } from "@/lib/workflow";
import { PrintDialog } from "@/components/banner/PrintDialog";
import { Printer, Lock, Unlock } from "lucide-react";

const fmt = (v, u) => Number(v).toFixed(u === "px" ? 0 : 2).replace(".", ",");

function BlockWidth({ block, space, onSet }) {
  const cur = ((block.w / 100) * space.width).toFixed(space.unit === "m" ? 2 : 0).replace(".", ",");
  const apply = (e) => {
    const v = parseFloat(e.target.value.replace(",", "."));
    if (!(v > 0)) { e.target.value = cur; return; }
    const w = Math.min(100, (v / space.width) * 100);
    if (Math.abs(w - block.w) > 0.001) onSet(w);
  };
  return (
    <label className="flex items-center gap-1.5 text-sm text-slate-600">Breite
      <Input key={`${block.id}-${cur}`} defaultValue={cur} onBlur={apply} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} inputMode="decimal" className="h-8 w-20 bg-white text-right" data-testid="banner-block-width" />
      <span>{space.unit}</span>
    </label>
  );
}

export function BannerEditor({ spaceId, onSaved }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [picker, setPicker] = useState(null);
  const [count, setCount] = useState(8);
  const [printOpen, setPrintOpen] = useState(false);
  useEffect(() => { api.get(`/banner/${spaceId}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [spaceId]);
  const byId = useMemo(() => Object.fromEntries((data?.partners || []).map((p) => [p.id, p])), [data]);
  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const { space, slots, blocks } = data;
  const ratio = ratioOf(space);
  const nums = numbering(slots);
  const usedOn = {};
  slots.forEach((s) => { if (s.customer_id) (usedOn[s.customer_id] = usedOn[s.customer_id] || []).push(nums[s.id]); });

  const save = async (nextSlots, nextBlocks = blocks, msg) => {
    setData({ ...data, slots: nextSlots, blocks: nextBlocks });
    try {
      const r = await api.put(`/banner/${spaceId}`, { slots: nextSlots, blocks: nextBlocks });
      setData(r.data); onSaved?.(); if (msg) toast.success(msg);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); api.get(`/banner/${spaceId}`).then((r) => setData(r.data)); }
  };
  const commit = (kind, box) => {
    if (kind === "slot") {
      if (blocks.some((b) => overlaps(box, b))) { toast.error("Das Logofeld liegt in einem Sperrbereich – bitte an eine freie Stelle schieben"); return; }
      save(slots.map((s) => (s.id === box.id ? box : s)));
    } else save(slots, blocks.map((b) => (b.id === box.id ? box : b)));
    setSel({ kind, id: box.id });
  };
  const select = (s) => {
    setSel(s && { kind: s.kind, id: s.id });
    if (s?.click && s.kind === "slot" && !slots.find((x) => x.id === s.id)?.customer_id) setPicker(s.id);
  };
  const fitTo = (s, cid) => {
    const p = byId[cid];
    const a = (p?.print?.preview_id && p.print.aspect) || p?.logo?.aspect;
    if (!a) return s;
    let w = (a * s.h) / ratio; let h = s.h;
    if (s.x + w > 100) { w = 100 - s.x; h = (w * ratio) / a; }
    return { ...s, w, h, y: (100 - h) / 2 };
  };
  const pick = (cid) => {
    const p = byId[cid];
    save(slots.map((s) => (s.id === picker ? (cid ? fitTo({ ...s, customer_id: cid }, cid) : { ...s, customer_id: null }) : s)), blocks, cid ? `${p?.name} auf Feld ${nums[picker]} – im Profil als gebucht eingetragen` : "Feld geleert");
    setPicker(null);
  };
  const addSlot = () => {
    const s = freeSpot(slots, blocks, ratio);
    if (!s) { toast.error("Kein freier Platz mehr – bitte Felder verkleinern oder „Gleichmäßig verteilen“ nutzen"); return; }
    save([...slots, s]);
  };
  const removeSlot = (id) => {
    const s = slots.find((x) => x.id === id);
    if (s.customer_id && !window.confirm(`Feld ${nums[id]} mit ${byId[s.customer_id]?.name} entfernen?`)) return;
    save(slots.filter((x) => x.id !== id)); setSel(null);
  };
  const removeLast = () => {
    const order = [...slots].sort((a, b) => nums[b.id] - nums[a.id]);
    const victim = order.find((s) => !s.customer_id) || order[0];
    if (victim) removeSlot(victim.id);
  };
  const arrange = () => {
    const next = respace(slots, blocks);
    if (!next) { toast.error("Die Logofelder passen so nicht nebeneinander – bitte einzelne Felder verkleinern"); return; }
    save(next, blocks, "Abstände gleichmäßig verteilt – Größen bleiben erhalten");
  };
  const addBlock = () => { const b = { id: uid(), x: 45, y: 0, w: 6, h: 100, label: "Sperrbereich" }; save(slots, [...blocks, b], "Sperrbereich angelegt – an die richtige Stelle schieben"); setSel({ kind: "block", id: b.id }); };
  const selSlot = sel?.kind === "slot" && slots.find((s) => s.id === sel.id);
  const selBlock = sel?.kind === "block" && blocks.find((b) => b.id === sel.id);
  const taken = slots.filter((s) => s.customer_id).length;
  const expired = slots.filter((s) => termStatus(byId[s.customer_id], data.today)?.key === "expired").length;

  return (
    <div className="space-y-5" data-testid="banner-editor">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{space.type_name}</p>
          <h2 className="text-2xl font-semibold text-slate-900" data-testid="banner-title">{space.name}{space.location && <span className="text-slate-400 font-normal"> · {space.location}</span>}</h2>
          <p className="text-sm text-slate-500">{space.width && space.height ? `${fmt(space.width, space.unit)} × ${fmt(space.height, space.unit)} ${space.unit}` : "Maße im Leistungskatalog ergänzen"} · <b className="text-slate-800" data-testid="banner-occupancy">{taken} von {slots.length} Feldern belegt</b>{slots.length - taken > 0 && <span className="text-emerald-700"> · {slots.length - taken} frei</span>}{expired > 0 && <span className="text-red-600" data-testid="banner-expired-count"> · davon {expired} abgelaufen</span>}</p>
        </div>
        {slots.length > 0 && <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full border border-slate-200 bg-white">
            <button type="button" onClick={removeLast} className="h-8 w-8 grid place-items-center text-slate-600 hover:text-slate-900" data-testid="banner-slot-minus"><Minus size={14} /></button>
            <span className="px-1 text-sm tabular-nums" data-testid="banner-slot-count">{slots.length} Logofelder</span>
            <button type="button" onClick={addSlot} disabled={slots.length >= 60} className="h-8 w-8 grid place-items-center text-slate-600 hover:text-slate-900" data-testid="banner-slot-plus"><Plus size={14} /></button>
          </div>
          <Button size="sm" variant="outline" onClick={arrange} data-testid="banner-arrange"><WandSparkles size={14} className="mr-1.5" /> Gleichmäßig verteilen</Button>
          <Button size="sm" variant="outline" onClick={addBlock} data-testid="banner-add-block"><SquareDashed size={14} className="mr-1.5" /> Sperrbereich</Button>
          <Button size="sm" variant="outline" onClick={() => downloadBlob("/banner/export/pdf", { space_id: spaceId }, `Werbebanner ${space.name} ${data.today}.pdf`)} data-testid="banner-pdf"><FileDown size={14} className="mr-1.5" /> PDF</Button>
          <Button size="sm" onClick={() => setPrintOpen(true)} className="bg-[#005448] hover:bg-[#00433a]" data-testid="banner-print"><Printer size={14} className="mr-1.5" /> Druck-PDF</Button>
        </div>}
      </div>

      {slots.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-5 flex flex-wrap items-center gap-3" data-testid="banner-setup">
        <span className="text-sm text-slate-600">Wie viele Logos sind auf diesem Banner vorgesehen?</span>
        <Input type="number" min={1} max={60} value={count} onChange={(e) => setCount(e.target.value)} className="w-20 h-9" data-testid="banner-setup-count" />
        <Button size="sm" className="bg-[#005448] hover:bg-[#00433a]" onClick={() => save(distribute(Math.min(60, Math.max(1, Number(count) || 1)), blocks, ratio), blocks, "Logofelder angelegt")} data-testid="banner-setup-create">Logofelder anlegen</Button>
        <Button size="sm" variant="outline" onClick={addBlock} data-testid="banner-setup-block"><SquareDashed size={14} className="mr-1.5" /> Zuerst Sperrbereich setzen</Button>
      </div>}

      <BannerCanvas ratio={ratio} slots={slots} blocks={blocks} byId={byId} nums={nums} today={data.today} selected={sel} onSelect={select} onCommit={commit} />
      <Legend />

      {selSlot && <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="banner-selected-slot">
        <span className="text-sm font-semibold">Feld {nums[selSlot.id]}</span>
        <span className="text-sm text-slate-600">{byId[selSlot.customer_id]?.name || "frei"}</span>
        <span className="flex-1" />
        {selSlot.customer_id && <Button size="sm" variant="outline" onClick={() => save(slots.map((s) => (s.id === selSlot.id ? fitTo(s, s.customer_id) : s)), blocks, "Feld an Logo angepasst")} data-testid="banner-selected-fit">Feld an Logo anpassen</Button>}
        <Button size="sm" variant="outline" onClick={() => setPicker(selSlot.id)} data-testid="banner-selected-pick"><UserRound size={14} className="mr-1.5" /> {selSlot.customer_id ? "Partner ändern" : "Partner wählen"}</Button>
        <Button size="sm" variant="outline" onClick={() => removeSlot(selSlot.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="banner-selected-delete"><Trash2 size={14} className="mr-1.5" /> Feld löschen</Button>
      </div>}
      {selBlock && <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3" data-testid="banner-selected-block">
        <span className="text-sm font-semibold">Sperrbereich</span>
        <Input defaultValue={selBlock.label} key={selBlock.id} onBlur={(e) => e.target.value !== selBlock.label && save(slots, blocks.map((b) => (b.id === selBlock.id ? { ...b, label: e.target.value } : b)))} placeholder="z. B. Pfosten, Netzpfosten" className="h-8 w-56 bg-white" data-testid="banner-block-label" />
        {space.width > 0 && !selBlock.locked && <BlockWidth block={selBlock} space={space} onSet={(w) => save(slots, blocks.map((b) => (b.id === selBlock.id ? { ...b, w, x: Math.min(b.x, 100 - w) } : b)))} />}
        <span className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => save(slots, blocks.map((b) => (b.id === selBlock.id ? { ...b, locked: !b.locked } : b)), selBlock.locked ? "Fixierung gelöst – Sperrbereich kann geändert werden" : "Sperrbereich fixiert")} className={selBlock.locked ? "border-amber-300 bg-amber-100 text-amber-900" : ""} data-testid="banner-block-lock">
          {selBlock.locked ? <><Unlock size={14} className="mr-1.5" /> Fixierung lösen</> : <><Lock size={14} className="mr-1.5" /> Fixieren</>}
        </Button>
        {!selBlock.locked && <Button size="sm" variant="outline" onClick={() => { save(slots, blocks.filter((b) => b.id !== selBlock.id), "Sperrbereich entfernt"); setSel(null); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="banner-block-delete"><Trash2 size={14} className="mr-1.5" /> Entfernen</Button>}
      </div>}

      <SlotList slots={slots} nums={nums} byId={byId} partners={data.partners} usedOn={usedOn} today={data.today} onOpen={(id) => { setSel({ kind: "slot", id }); setPicker(id); }} />
      <PrintDialog open={printOpen} onClose={() => setPrintOpen(false)} space={space} today={data.today} />
      <SlotPicker open={!!picker} onClose={() => setPicker(null)} num={nums[picker]} partners={data.partners} current={slots.find((s) => s.id === picker)?.customer_id} usedOn={usedOn} onPick={pick} />
    </div>
  );
}
